/**
 * **What the trainer can DO about one queue item, from the home.**
 *
 * One decision, taken once, for all fifteen reasons this app can name: which control the row draws
 * and where it goes.
 *
 * Pure. `t` never arrives; what leaves here is an i18n KEY the row translates.
 *
 * ── A clip and a comment under one open the ONE player ──────────────────────────────────────
 * Both lead to the client's card with `?clip=`, where the full-screen player plays the film, marks
 * it seen on its first frame and carries the discussion. The seen mark resolves a new-clip item
 * (`exercise-clip-timeline` R9/R18), and a reply written there resolves a comment waiting on one
 * (R15).
 *
 * ── Marking a survey reviewed is NOT refused ─────────────────────────────────────────────────
 * One idempotent 204 write, exactly what `intake-survey` R28 calls implicit resolution, so no screen
 * is needed to close the item. It is not "Gotowe" under another name: a review is visible to the
 * CLIENT (`intake-survey` R31), where a queue response is the trainer's private sentence about their
 * own list (attention-queue R4). "Gotowe" and "Później" keep working on every reason.
 *
 * ── `invitationPending`'s primary still opens the roster ──────────────────────────────────────
 * The Pulpit's one-line row keeps it. The task CARD (`ui/InvitationBlock`) resends on the spot since
 * 24 September 2026, through `features/invite-client`'s `useResendInvitation`, gated on
 * `entities/client`'s `canResendInvitation` exactly as the roster's actions menu is.
 *
 * ── The composer opens on an EMPTY field, and the client's own note stands above it ──────────
 * `clientNotePreview` is the CLIENT's note on that session, not the trainer's. The trainer's note is
 * what the composer edits, and seeding the field with the client's words made the trainer's first
 * act deleting them — or, worse, saving them back as their own. The detector only fires while the
 * session has no trainer note at all (`TrainerNote == null`), so an empty baseline is a FACT about
 * every item that reaches this screen rather than a guess: the composer's `savedComment` is `''`,
 * which is what it means by "never commented on".
 *
 * What the preview is good for is context, so it leaves here as `clientNote` and the row draws it
 * as a quoted line above the field.
 *
 * ── Why `planEndingSoon` opens the library rather than the assign dialog ─────────────────────
 * `AssignPlanDialog` needs a `planId` to open at all — it assigns a NAMED plan to clients, not a
 * client to a plan the trainer has yet to choose. From the home nobody has chosen one, so the
 * trainer goes where the choosing happens.
 */

import type { AttentionReason } from '~/entities/client';
import type { TrainWithMeModulesAttentionDtosResponsesAttentionItemPayload as AttentionItemPayload } from '~/shared/api/generated/model';

/** The glyph the row's tile draws. Names a CONCEPT; `ui/HomeGlyphs` owns the drawing. */
export type TaskGlyph =
  | 'dumbbell'
  | 'form'
  | 'calendarEmpty'
  | 'half'
  | 'pulse'
  | 'flag'
  | 'calendarX'
  | 'hourglass'
  | 'mail'
  | 'target'
  | 'chat'
  | 'video'
  | 'camera'
  | 'sleep'
  | 'gauge'
  | 'trophy';

/**
 * The row's primary control.
 *
 * A discriminated union rather than a `{ to: string }` bag: TanStack Router types `Link`'s `to` as
 * a union of the generated route paths, so naming the destinations as variants is what makes a
 * typo fail `tsc` instead of producing a dead link.
 */
export type TaskAction =
  | {
      readonly kind: 'sessionNote';
      readonly labelKey: string;
      readonly workoutExecutionId: number;
      /**
       * The CLIENT's own note on that session, for the quoted line above the field. `''` where they
       * left none. Never the composer's baseline — see the header.
       */
      readonly clientNote: string;
    }
  | { readonly kind: 'openPlans'; readonly labelKey: string }
  | { readonly kind: 'openClient'; readonly labelKey: string; readonly clientId: number }
  | { readonly kind: 'openRoster'; readonly labelKey: string }
  /**
   * A public-form lead (public-form R20): the lead's own page, or the inbox when the item named no
   * lead. `leadId` is the payload's `publicFormLeadId` (`leadIdOf`); an older backend omits it.
   */
  | { readonly kind: 'openLead'; readonly labelKey: string; readonly leadId: number | null }
  /** `/czat`: the chat module's screen, which says the conversation is in the mobile app. */
  | { readonly kind: 'openChat'; readonly labelKey: string }
  /**
   * Record that the trainer has looked at a submitted survey — `POST /survey/assignment/{id}/
   * mark-reviewed`, no body, 204, idempotent. Visible to the client (`intake-survey` R31), which is
   * what keeps it distinct from "Gotowe".
   */
  | { readonly kind: 'markReviewed'; readonly labelKey: string; readonly surveyAssignmentId: number }
  /** The client's card with the full-screen player open on this clip. See the header. */
  | {
      readonly kind: 'openClip';
      readonly labelKey: string;
      /**
       * What the trainer goes there to do: watch a film they have not seen, or answer a comment
       * under one. Same destination, a different word and a different drawing on the control.
       */
      readonly intent: 'watch' | 'note';
      readonly clientId: number;
      readonly clipId: number;
    };

/**
 * Where `openClip` leads from a surface that is NOT the client's card — `/zadania` and the Pulpit:
 * the client's card on Forma, with the one player open on the clip. The client's own Przegląd opens
 * the player in place instead (`TaskSlots.openClip`), keeping its roster list and its tab.
 */
export function clipDestination(action: { readonly clientId: number; readonly clipId: number }) {
  return {
    to: '/klienci/$clientId',
    params: { clientId: String(action.clientId) },
    search: { tab: 'forma', clip: action.clipId },
  } as const;
}

/** „Kalendarz klienta" on an empty calendar's desk card: the client's card on its Kalendarz tab. */
export function calendarDestination(clientId: number) {
  return {
    to: '/klienci/$clientId',
    params: { clientId: String(clientId) },
    search: { tab: 'calendar' },
  } as const;
}

/**
 * Where a survey task's „Przejrzyj" leads from a surface that is NOT the client's card — `/zadania`
 * and the Pulpit: the client's Ankiety with the submitted occurrence open (owner, 25 September
 * 2026). The client's own Przegląd switches its tab in place instead, keeping its roster list.
 */
export function surveyDestination(clientId: number, surveyAssignmentId: number) {
  return {
    to: '/klienci/$clientId',
    params: { clientId: String(clientId) },
    search: { tab: 'surveys', survey: surveyAssignmentId },
  } as const;
}

/**
 * One drawing per KIND of task — KANON "Karty zadań" (owner, 24 September 2026). Until then several
 * kinds shared one: three Feedback rows wore the note, a missed session and an empty calendar the
 * same crossed-out calendar, a plan ending and a plan to close the same clock. On the new cards the
 * glyph is the kind's name at a glance, beside its own ink (`ui/reasonTile.module.css`), so two
 * kinds cannot share one.
 */
const GLYPH: Readonly<Record<AttentionReason, TaskGlyph>> = {
  missedWorkout: 'calendarX',
  abandonedWorkout: 'half',
  invitationPending: 'mail',
  goalRenewalDue: 'target',
  planEndingSoon: 'flag',
  planCompletionDue: 'flag',
  unreadMessage: 'chat',
  noUpcomingSessions: 'calendarEmpty',
  lapsedActivity: 'hourglass',
  sessionAwaitingReview: 'dumbbell',
  surveyAwaitingReview: 'form',
  goalAchieved: 'target',
  planCompleted: 'pulse',
  newClip: 'video',
  // A question under a film is about the film (owner, 26 September 2026): a camera, not the chat's
  // bubble, which stays the unread message's alone.
  clipCommentAwaitingReply: 'camera',
  // A filled-in form, like a submitted survey.
  publicFormLeadSubmitted: 'form',
  lowSleep: 'sleep',
  lowEnergy: 'gauge',
  prAchieved: 'trophy',
};

export function taskGlyph(reason: AttentionReason): TaskGlyph {
  return GLYPH[reason];
}

/** Every label this module can hand back, under one prefix so a missing one is one grep. */
const LABEL = {
  writeNote: 'web:home.action.writeNote',
  assignPlan: 'web:home.action.assignPlan',
  openClient: 'web:home.action.openClient',
  openInvitation: 'web:home.action.openInvitation',
  writeDm: 'web:home.action.writeDm',
  leaveNote: 'web:home.action.leaveNote',
  markReviewed: 'web:home.action.markReviewed',
  watchClip: 'web:home.action.watchClip',
  openLead: 'web:publicForm.leads.attention.action',
  congratulate: 'web:home.action.congratulate',
} as const;

/**
 * The lead a `PublicFormLeadSubmitted` item is about, off the payload. A payload without one opens
 * the inbox rather than a dead address. The filler's name is the item's `subject.person`, as for
 * a pending invitation.
 */
export function leadIdOf(payload: AttentionItemPayload | null | undefined): number | null {
  return typeof payload?.publicFormLeadId === 'number' ? payload.publicFormLeadId : null;
}

/**
 * The primary control for one item.
 *
 * `subjectCustomerId` is the item's own subject and is `null` for exactly one reason — the pending
 * invitation, whose invitee is not a client yet (R10). Every "open the client" arm therefore falls
 * back to the roster when it has no id, rather than routing to `/klienci/0`.
 *
 * `self`: the item is about the trainer's own client profile. Its chat arms open the client
 * instead — the backend refuses a conversation with yourself (`conversation_with_self`).
 */
export function taskAction(
  reason: AttentionReason,
  payload: AttentionItemPayload | null | undefined,
  subjectCustomerId: number | null,
  self = false,
): TaskAction {
  if (self && (reason === 'unreadMessage' || reason === 'prAchieved')) return openClient(subjectCustomerId);
  switch (reason) {
    case 'sessionAwaitingReview': {
      const workoutExecutionId = payload?.workoutExecutionId;
      // No execution id means no session to comment on. The item is still real — the trainer can
      // read the client's session from their detail — so it degrades to the client rather than to
      // a composer with nothing behind it.
      if (typeof workoutExecutionId !== 'number') return openClient(subjectCustomerId);
      return {
        kind: 'sessionNote',
        labelKey: LABEL.writeNote,
        workoutExecutionId,
        clientNote: payload?.clientNotePreview ?? '',
      };
    }
    // `planCompleted` joins the other three: a self-paced plan that ran out of occurrences and
    // closed itself needs nothing closed — the trainer is being asked for the next plan, which is
    // the same destination and the same word on the control.
    case 'planEndingSoon':
    case 'planCompletionDue':
    case 'noUpcomingSessions':
    case 'planCompleted':
      return { kind: 'openPlans', labelKey: LABEL.assignPlan };
    case 'invitationPending':
      return { kind: 'openRoster', labelKey: LABEL.openInvitation };
    case 'unreadMessage':
      // The chat's own word on every surface (owner, 25 September 2026): an unread message and the
      // desk's way to `/czat` beside every card are the same act, so they say the same thing.
      return { kind: 'openChat', labelKey: LABEL.writeDm };
    case 'surveyAwaitingReview': {
      const surveyAssignmentId = payload?.surveyAssignmentId;
      // No assignment id is nothing to mark. The item is still real, so it degrades to the client
      // rather than to a control that would post to `/survey/assignment/undefined/mark-reviewed`.
      if (typeof surveyAssignmentId !== 'number') return openClient(subjectCustomerId);
      return { kind: 'markReviewed', labelKey: LABEL.markReviewed, surveyAssignmentId };
    }
    // Both open the one player. A comment is answered in the player's discussion, and the word says
    // so (owner, 25 September 2026: „Zostaw notatkę", not the chat's „Odpisz").
    case 'newClip':
    case 'clipCommentAwaitingReply': {
      const clipId = payload?.clipId;
      if (typeof clipId !== 'number' || subjectCustomerId === null) {
        return openClient(subjectCustomerId);
      }
      return {
        kind: 'openClip',
        labelKey: reason === 'newClip' ? LABEL.watchClip : LABEL.leaveNote,
        intent: reason === 'newClip' ? 'watch' : 'note',
        clientId: subjectCustomerId,
        clipId,
      };
    }
    case 'publicFormLeadSubmitted':
      return { kind: 'openLead', labelKey: LABEL.openLead, leadId: leadIdOf(payload) };
    // A record is a moment to write to the client, not work to resolve (R24): the primary goes
    // where „Napisz DM" goes, under its own word.
    case 'prAchieved':
      return { kind: 'openChat', labelKey: LABEL.congratulate };
    // A low reading joins them: the readings are the client's own, and what the trainer does about
    // them is talk to the client.
    case 'goalRenewalDue':
    case 'missedWorkout':
    case 'lapsedActivity':
    case 'abandonedWorkout':
    case 'goalAchieved':
    case 'lowSleep':
    case 'lowEnergy':
      return openClient(subjectCustomerId);
  }
}

function openClient(clientId: number | null): TaskAction {
  if (clientId === null) return { kind: 'openRoster', labelKey: LABEL.openClient };
  return { kind: 'openClient', labelKey: LABEL.openClient, clientId };
}

/**
 * The two task cards that decide where the primary stands: `/zadania`'s wide card (`desk`) and the
 * client Przegląd's Talia card (`quick`). The Pulpit's short card is not one of them — its foot
 * carries every kind's primary, as its rows always did.
 */
export type TaskCardSurface = 'desk' | 'quick';

/** Where every surface keeps the primary in the card's own controls. */
const FOOT_ON_EVERY_CARD: ReadonlySet<TaskAction['kind']> = new Set([
  'markReviewed',
  'openChat',
  'openClip',
  // A lead has no body to act in; the way to it is the foot's.
  'openLead',
]);

/**
 * Does this kind's primary stand in the card's foot (its bar under `/zadania`'s card, its foot on the
 * Przegląd), or does the card's body already do the act?
 *
 * On EVERY card: the survey's „Oznacz jako przejrzane", a write the body has no place for; the chat's
 * way to `/czat`; and the clip's way into the one player on the client's card, because the quick
 * card's body draws the film's poster and never plays it — the poster is a mouse shortcut to the same
 * place and takes no tab stop, so the link in the foot is the keyboard's. On the Przegląd's card ALSO
 * the session note, because the quick card has no composer in its body — its „Skomentuj sesję" opens
 * the session panel. Every other kind acts inside the body (the plan, goal, invitation and calendar
 * blocks) or has nothing to do from the card that the page the trainer is on does not already do —
 * „Otwórz klienta" on the client's own Przegląd.
 *
 * One exception on the desk: a new clip. Its body plays the film in place, and the small player's
 * own „Analizuj" is the one way into the full player (owner, 26 September 2026), so a second button
 * to the same place would only repeat it. A comment under a clip keeps its foot on the desk — there
 * the primary takes the trainer to the reply field beside the film, not to the player.
 */
export function primaryStandsInFoot(action: TaskAction, surface: TaskCardSurface): boolean {
  if (surface === 'desk' && action.kind === 'openClip' && action.intent === 'watch') return false;
  if (FOOT_ON_EVERY_CARD.has(action.kind)) return true;
  return surface === 'quick' && action.kind === 'sessionNote';
}
